import React, { useState, useEffect } from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {
    Search,
    MapPin,
    Building,
    Grid,
    List,
    Map,
    ArrowUpDown,
    Filter,
    Bed,
    Bath,
    Square,
    Heart,
    ChevronDown,
    Star,
    Phone,
    Loader,
    AlertCircle
} from 'lucide-react';
import { usePropertyListings } from '../../hooks/usePropertyListings';
import SEO from '../../Components/Common/SEO';
import PropertyCard from '../../Components/Common/PropertyCard';
import { useProperty } from '../../App';

// Locations for dropdown
const locations = [
    "Westlands", "Karen", "Kilimani", "Lavington", "Runda", "Parklands",
    "Langata", "Upper Hill", "Hurlingam", "Kileleshwa", "South B", "South C"
];

// Property types for dropdown
const propertyTypes = [
    "apartment", "house", "studio", "penthouse", "villa", "townhouse"
];

const SORT_MAPPING = {
    newest: { sortBy: 'date_created', sortOrder: 'desc' },
    price_low: { sortBy: 'price', sortOrder: 'asc' },
    price_high: { sortBy: 'price', sortOrder: 'desc' },
    rating: { sortBy: 'rating', sortOrder: 'desc' },
    size_small: { sortBy: 'area', sortOrder: 'asc' },
    size_large: { sortBy: 'area', sortOrder: 'desc' }
};

const SearchResults = () => {
    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();
    const [viewMode, setViewMode] = useState('grid'); // grid, list, map
    const [searchQuery, setSearchQuery] = useState(searchParams.get('query') || '');
    const [selectedLocation, setSelectedLocation] = useState(searchParams.get('location') || '');
    const [selectedPropertyType, setSelectedPropertyType] = useState(searchParams.get('propertyType') || '');
    const [priceRange, setPriceRange] = useState({
        min: searchParams.get('minPrice') || '',
        max: searchParams.get('maxPrice') || ''
    });
    const [bedroomCount, setBedroomCount] = useState(searchParams.get('bedrooms') || 'any');
    const [bathroomCount, setBathroomCount] = useState(searchParams.get('bathrooms') || 'any');
    const [sortOption, setSortOption] = useState(searchParams.get('sort') || 'newest');
    const [filtersVisible, setFiltersVisible] = useState(false);
    const { savedProperties, toggleSaveProperty } = useProperty();

    const buildApiFilters = () => ({
        page: 1,
        limit: 24,
        search: searchQuery,
        location: selectedLocation,
        propertyType: selectedPropertyType,
        bedrooms: bedroomCount === 'any' ? '' : bedroomCount.replace('+', ''),
        bathrooms: bathroomCount === 'any' ? '' : bathroomCount.replace('+', ''),
        minPrice: priceRange.min,
        maxPrice: priceRange.max,
        ...(SORT_MAPPING[sortOption] || SORT_MAPPING.newest)
    });

    const { properties: filteredProperties, pagination, loading, error, updateFilters } =
        usePropertyListings(buildApiFilters());
    const totalResults = pagination.totalProperties ?? filteredProperties.length;

    // Re-fetch and sync URL params whenever filter options change
    useEffect(() => {
        updateFilters(buildApiFilters());
        updateSearchParams();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocation, selectedPropertyType, priceRange, bedroomCount, bathroomCount, sortOption, searchQuery]);

    // Update URL search parameters
    const updateSearchParams = () => {
        const params = new URLSearchParams();

        if (searchQuery) params.set('query', searchQuery);
        if (selectedLocation) params.set('location', selectedLocation);
        if (selectedPropertyType) params.set('propertyType', selectedPropertyType);
        if (priceRange.min) params.set('minPrice', priceRange.min);
        if (priceRange.max) params.set('maxPrice', priceRange.max);
        if (bedroomCount !== 'any') params.set('bedrooms', bedroomCount);
        if (bathroomCount !== 'any') params.set('bathrooms', bathroomCount);
        if (sortOption !== 'newest') params.set('sort', sortOption);

        setSearchParams(params);
    };

    // Handle save/unsave property
    const handleSaveProperty = (propertyId) => {
        toggleSaveProperty(propertyId);
    };

    // Handle search form submission
    const handleSearch = (e) => {
        e.preventDefault();
        updateFilters(buildApiFilters());
    };

    // Reset all filters
    const resetFilters = () => {
        setSearchQuery('');
        setSelectedLocation('');
        setSelectedPropertyType('');
        setPriceRange({ min: '', max: '' });
        setBedroomCount('any');
        setBathroomCount('any');
        setSortOption('newest');
    };

    const handleNavigation = (path) => {
        navigate(path);
    }

    const handlePageChange = (page) => {
        updateFilters({ ...buildApiFilters(), page });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="min-h-screen bg-karibu-100">
            <SEO
                title="Search Properties"
                description="Search for rental properties in Kenya by location, price range, bedrooms, and more. Find your perfect home with Rent Utopia."
                canonicalPath="/search"
                noindex
            />
            {/* Search Header */}
            <section className="relative bg-highland-700 text-karibu-100 py-12 overflow-hidden">
                <div
                    aria-hidden="true"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[360px] rounded-arch bg-gradient-to-b from-dawn-500/15 to-transparent blur-2xl"
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8">
                        <h1 className="font-display text-3xl md:text-4xl font-bold mb-4">Find Your Perfect Home</h1>
                        <p className="text-lg text-karibu-200/80">
                            {totalResults} properties matching your search criteria
                        </p>
                    </div>

                    {/* Search Form */}
                    <div className="bg-karibu-100 rounded-arch pt-10 pb-6 px-6 shadow-lg mb-6">
                        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-soil-400 w-5 h-5" />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search by location, property type, features..."
                                        className="w-full pl-10 pr-4 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent text-soil-900 bg-white"
                                    />
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => setFiltersVisible(!filtersVisible)}
                                className="bg-soil-100 hover:bg-soil-100/70 text-soil-600 font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                            >
                                <Filter className="w-5 h-5" />
                                <span>Filters</span>
                            </button>

                            <button
                                type="submit"
                                className="bg-highland-500 hover:bg-highland-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                            >
                                <Search className="w-5 h-5" />
                                <span>Search</span>
                            </button>
                        </form>

                        {/* Advanced Filters */}
                        <div className={`${filtersVisible ? 'block' : 'hidden'} pt-6 border-t border-soil-100 mt-6`}>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {/* Location Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-soil-600 mb-2">Location</label>
                                    <div className="relative">
                                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-soil-400 w-5 h-5" />
                                        <select
                                            value={selectedLocation}
                                            onChange={(e) => setSelectedLocation(e.target.value)}
                                            className="w-full pl-10 pr-10 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent text-soil-900 bg-white appearance-none"
                                        >
                                            <option value="">All Locations</option>
                                            {locations.map(location => (
                                                <option key={location} value={location}>{location}</option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-soil-400 w-5 h-5 pointer-events-none" />
                                    </div>
                                </div>

                                {/* Property Type Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-soil-600 mb-2">Property Type</label>
                                    <div className="relative">
                                        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-soil-400 w-5 h-5" />
                                        <select
                                            value={selectedPropertyType}
                                            onChange={(e) => setSelectedPropertyType(e.target.value)}
                                            className="w-full pl-10 pr-10 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent text-soil-900 bg-white appearance-none"
                                        >
                                            <option value="">All Property Types</option>
                                            {propertyTypes.map(type => (
                                                <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-soil-400 w-5 h-5 pointer-events-none" />
                                    </div>
                                </div>

                                {/* Price Range */}
                                <div>
                                    <label className="block text-sm font-medium text-soil-600 mb-2">Price Range (KES)</label>
                                    <div className="flex space-x-2">
                                        <input
                                            type="number"
                                            value={priceRange.min}
                                            onChange={(e) => setPriceRange({...priceRange, min: e.target.value})}
                                            placeholder="Min"
                                            className="w-1/2 pl-3 pr-3 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent text-soil-900 bg-white"
                                        />
                                        <input
                                            type="number"
                                            value={priceRange.max}
                                            onChange={(e) => setPriceRange({...priceRange, max: e.target.value})}
                                            placeholder="Max"
                                            className="w-1/2 pl-3 pr-3 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent text-soil-900 bg-white"
                                        />
                                    </div>
                                </div>

                                {/* Bedrooms & Bathrooms */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-soil-600 mb-2">Bedrooms</label>
                                        <select
                                            value={bedroomCount}
                                            onChange={(e) => setBedroomCount(e.target.value)}
                                            className="w-full pl-3 pr-10 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent text-soil-900 bg-white appearance-none"
                                        >
                                            <option value="any">Any</option>
                                            <option value="0">Studio</option>
                                            <option value="1">1</option>
                                            <option value="2">2</option>
                                            <option value="3">3</option>
                                            <option value="4+">4+</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-soil-600 mb-2">Bathrooms</label>
                                        <select
                                            value={bathroomCount}
                                            onChange={(e) => setBathroomCount(e.target.value)}
                                            className="w-full pl-3 pr-10 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent text-soil-900 bg-white appearance-none"
                                        >
                                            <option value="any">Any</option>
                                            <option value="1">1</option>
                                            <option value="2">2</option>
                                            <option value="3+">3+</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Filter Actions */}
                            <div className="flex justify-end mt-6">
                                <button
                                    type="button"
                                    onClick={resetFilters}
                                    className="mr-4 text-soil-600 hover:text-soil-900 font-medium"
                                >
                                    Reset Filters
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFiltersVisible(false)}
                                    className="bg-highland-500 hover:bg-highland-600 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-200"
                                >
                                    Apply Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Results Section */}
            <section className="py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Results Header */}
                    <div className="flex flex-col md:flex-row justify-between items-center mb-8">
                        <div className="mb-4 md:mb-0">
                            <h2 className="font-display text-2xl font-bold text-soil-900">
                                {totalResults} Properties Found
                            </h2>
                            <p className="text-soil-600">
                                {selectedLocation ? `in ${selectedLocation}` : 'in Kenya'}
                                {selectedPropertyType ? ` • ${selectedPropertyType.charAt(0).toUpperCase() + selectedPropertyType.slice(1)}` : ''}
                            </p>
                        </div>

                        <div className="flex items-center space-x-4">
                            {/* View Mode Toggles */}
                            <div className="bg-white rounded-lg border border-soil-100 flex">
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`p-2 ${viewMode === 'grid' ? 'bg-highland-50 text-highland-500' : 'text-soil-600'}`}
                                >
                                    <Grid className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`p-2 ${viewMode === 'list' ? 'bg-highland-50 text-highland-500' : 'text-soil-600'}`}
                                >
                                    <List className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => setViewMode('map')}
                                    className={`p-2 ${viewMode === 'map' ? 'bg-highland-50 text-highland-500' : 'text-soil-600'}`}
                                >
                                    <Map className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Sort Options */}
                            <div className="relative">
                                <div className="flex items-center space-x-2 bg-white rounded-lg border border-soil-100 p-2">
                                    <ArrowUpDown className="w-4 h-4 text-soil-600" />
                                    <select
                                        value={sortOption}
                                        onChange={(e) => setSortOption(e.target.value)}
                                        className="appearance-none bg-transparent border-none text-soil-600 pr-8 py-1 focus:outline-none"
                                    >
                                        <option value="newest">Newest</option>
                                        <option value="price_low">Price: Low to High</option>
                                        <option value="price_high">Price: High to Low</option>
                                        <option value="rating">Highest Rated</option>
                                        <option value="size_small">Size: Small to Large</option>
                                        <option value="size_large">Size: Large to Small</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Loading State */}
                    {loading ? (
                        <div className="flex justify-center items-center py-20">
                            <Loader className="w-10 h-10 text-highland-500 animate-spin" />
                            <span className="ml-3 text-xl text-soil-600">Loading properties...</span>
                        </div>
                    ) : error ? (
                        <div className="bg-white rounded-xl shadow-md p-8 text-center">
                            <div className="flex justify-center mb-4">
                                <AlertCircle className="w-16 h-16 text-murram-400" />
                            </div>
                            <h3 className="font-display text-2xl font-bold text-soil-900 mb-2">Failed to Load Properties</h3>
                            <p className="text-soil-600">{error}</p>
                        </div>
                    ) : (
                        <>
                            {/* No Results State */}
                            {filteredProperties.length === 0 ? (
                                <div className="bg-white rounded-xl shadow-md p-8 text-center">
                                    <div className="flex justify-center mb-4">
                                        <Search className="w-16 h-16 text-soil-400" />
                                    </div>
                                    <h3 className="font-display text-2xl font-bold text-soil-900 mb-2">No Properties Found</h3>
                                    <p className="text-soil-600 mb-6">
                                        We couldn't find any properties matching your search criteria. Try adjusting your filters.
                                    </p>
                                    <button
                                        onClick={resetFilters}
                                        className="bg-highland-500 hover:bg-highland-600 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-200"
                                    >
                                        Reset Filters
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* Map View */}
                                    {viewMode === 'map' && (
                                        <div className="bg-white rounded-xl shadow-md p-4 mb-8">
                                            <div className="h-[600px] w-full bg-soil-50 rounded-lg flex items-center justify-center">
                                                <div className="text-center">
                                                    <Map className="w-16 h-16 text-soil-400 mx-auto mb-4" />
                                                    <h3 className="font-display text-xl font-semibold text-soil-600">Map View</h3>
                                                    <p className="text-soil-400 mt-2">Map integration would be implemented here</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Grid View */}
                                    {viewMode === 'grid' && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                            {filteredProperties.map((property) => (
                                                <PropertyCard
                                                    key={property.id}
                                                    property={property}
                                                    onClick={() => handleNavigation(`/property/${property.id}`)}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {/* List View */}
                                    {viewMode === 'list' && (
                                        <div className="space-y-6">
                                            {filteredProperties.map((property) => (
                                                <div
                                                    key={property.id}
                                                    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 border border-soil-100"
                                                >
                                                    <div className="flex flex-col md:flex-row">
                                                        {/* Property Image */}
                                                        <div className="relative md:w-1/3 h-48 md:h-auto overflow-hidden rounded-xl m-3">
                                                            <img
                                                                src={property.image}
                                                                alt={property.title}
                                                                className="w-full h-full object-cover rounded-xl hover:scale-105 transition-transform duration-300"
                                                            />
                                                            {property.featured && (
                                                                <div className="absolute top-4 left-4 bg-jacaranda-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                                                                    Featured
                                                                </div>
                                                            )}
                                                            <button
                                                                onClick={() => handleSaveProperty(property.id)}
                                                                className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all duration-200"
                                                            >
                                                                <Heart className={`w-5 h-5 ${savedProperties.includes(property.id) ? 'text-murram-500 fill-current' : 'text-soil-600'}`} />
                                                            </button>
                                                            <div className="absolute bottom-4 left-4 bg-highland-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                                                                {property.available}
                                                            </div>
                                                        </div>

                                                        {/* Property Details */}
                                                        <div className="p-6 md:w-2/3">
                                                            <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                                                                <div>
                                                                    <h3 className="font-display text-xl font-semibold text-soil-900 mb-2">{property.title}</h3>
                                                                    <div className="flex items-center text-soil-600 mb-4">
                                                                        <MapPin className="w-4 h-4 mr-1 text-murram-500" />
                                                                        <span className="text-sm">{property.location}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="mb-4 md:mb-0 md:text-right">
                                                                    <div className="font-mono text-2xl font-bold text-highland-500 mb-1">{property.price}</div>
                                                                    <div className="flex items-center md:justify-end space-x-1">
                                                                        <Star className="w-4 h-4 text-dawn-500 fill-current" />
                                                                        <span className="text-sm text-soil-600">{property.rating} ({property.reviews})</span>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {property.amenities && property.amenities.length > 0 && (
                                                                <div className="mb-4">
                                                                    <div className="flex flex-wrap gap-2 mb-4">
                                                                        {property.amenities.slice(0, 4).map((amenity, index) => (
                                                                            <span key={index} className="bg-highland-50 text-highland-600 px-3 py-1 rounded-full text-sm">
                                      {amenity}
                                    </span>
                                                                        ))}
                                                                        {property.amenities.length > 4 && (
                                                                            <span className="bg-soil-50 text-soil-600 px-3 py-1 rounded-full text-sm">
                                      +{property.amenities.length - 4} more
                                    </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            <div className="flex flex-wrap justify-between">
                                                                {/* Property Features */}
                                                                <div className="flex items-center space-x-4 text-sm text-soil-600 mb-4 md:mb-0">
                                                                    <div className="flex items-center space-x-1">
                                                                        <Bed className="w-4 h-4" />
                                                                        <span>{property.bedrooms} {property.bedrooms === 1 ? 'Bed' : 'Beds'}</span>
                                                                    </div>
                                                                    <div className="flex items-center space-x-1">
                                                                        <Bath className="w-4 h-4" />
                                                                        <span>{property.bathrooms} {property.bathrooms === 1 ? 'Bath' : 'Baths'}</span>
                                                                    </div>
                                                                    <div className="flex items-center space-x-1">
                                                                        <Square className="w-4 h-4" />
                                                                        <span>{property.area}</span>
                                                                    </div>
                                                                </div>

                                                                {/* Action Buttons */}
                                                                <div className="flex space-x-2">
                                                                    <button
                                                                        onClick={() => handleNavigation(`/property/${property.id}`)}
                                                                        className="bg-highland-500 hover:bg-highland-600 text-white py-2 px-4 rounded-lg font-medium transition-colors duration-200"
                                                                    >
                                                                        View Details
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {/* Pagination */}
                    {filteredProperties.length > 0 && pagination.totalPages > 1 && (
                        <div className="mt-12 flex justify-center">
                            <div className="flex space-x-1">
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={!pagination.hasPrevPage}
                                    className="px-4 py-2 border border-soil-100 rounded-l-lg text-soil-600 hover:bg-soil-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                >
                                    <ChevronDown className="w-5 h-5 transform rotate-90" />
                                </button>
                                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                                    <button
                                        key={page}
                                        onClick={() => handlePageChange(page)}
                                        className={`px-4 py-2 border border-soil-100 font-mono ${
                                            page === pagination.currentPage ? 'bg-highland-500 text-white' : 'text-soil-600 hover:bg-soil-50'
                                        } transition-colors duration-200`}
                                    >
                                        {page}
                                    </button>
                                ))}
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={!pagination.hasNextPage}
                                    className="px-4 py-2 border border-soil-100 rounded-r-lg text-soil-600 hover:bg-soil-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                >
                                    <ChevronDown className="w-5 h-5 transform -rotate-90" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Similar Searches Section */}
            <section className="py-12 bg-soil-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="font-display text-2xl font-bold text-soil-900 mb-6">Similar Searches</h2>
                    <div className="flex flex-wrap gap-4">
                        {["Apartments in Westlands", "2 Bedroom Houses in Nairobi", "Furnished Apartments",
                            "Properties under 100K", "Luxury Homes in Karen", "Studio Apartments in Kilimani"].map((search, index) => (
                            <button
                                key={index}
                                onClick={() => setSearchQuery(search)}
                                className="bg-white hover:bg-highland-50 text-soil-600 hover:text-highland-500 px-6 py-3 rounded-lg shadow-sm transition-colors duration-200"
                            >
                                {search}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Get Help Section */}
            <section className="relative py-12 bg-highland-700 text-karibu-100 overflow-hidden">
                <div
                    aria-hidden="true"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-arch bg-gradient-to-b from-dawn-500/15 to-transparent blur-2xl"
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-display text-3xl font-bold mb-4">Need Help Finding Your Perfect Home?</h2>
                    <p className="text-xl text-karibu-200/80 mb-8 max-w-2xl mx-auto">
                        Our property experts can help you find the perfect place based on your requirements
                    </p>
                    <button
                        onClick={() => navigate('/contact')}
                        className="bg-karibu-100 hover:bg-white text-highland-700 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 inline-flex items-center space-x-2"
                    >
                        <Phone className="w-5 h-5" />
                        <span>Contact a Property Expert</span>
                    </button>
                </div>
            </section>
        </div>
    );
};

export default SearchResults;